import {
  CarrierRateFragment,
  CarrierRatePreviewFragment,
  CarrierRateWithActionsFragment,
  MovementDetailsFragment,
  MovementFragment,
  MovementPreviewFragment,
  MovementWithActionsQuery,
} from '@shared/generated/graphql';
import {
  CarrierRatePreview,
  Movement,
  MovementDetails,
  MovementEquipment,
  MovementPreview,
} from '@shared/types/movement';
import { CarrierRate } from '@shared/types/quote';
import { Rate } from 'clerk_common/types/rates';
import { isCoverageStatus } from 'clerk_common/types/voomaFreight/contractedMovement';
import { shipmentFromFragment } from './shipment';

export const movementPreviewFromFragment = (
  fragment: MovementPreviewFragment
): MovementPreview => {
  return {
    id: fragment.id,
    attributes:
      (fragment.attributes as Record<string, string> | null | undefined) ??
      undefined,
    stops:
      fragment.stops?.map((s) => ({
        location: {
          name: s.location.name ?? undefined,
          addressOne: s.location.addressOne ?? undefined,
          addressTwo: s.location.addressTwo ?? undefined,
          city: s.location.city ?? undefined,
          state: s.location.state ?? undefined,
          zip: s.location.zip ?? undefined,
          country: s.location.country ?? undefined,
        },
        dateTimeRanges: s.dateTimeRanges?.map((dtr) => ({
          start: {
            dateTime: dtr.start.dateTime,
            timezone: dtr.start.timezone ?? undefined,
            interpretationPolicy: dtr.start.interpretationPolicy,
          },
          end: dtr.end
            ? {
                dateTime: dtr.end.dateTime,
                timezone: dtr.end.timezone ?? undefined,
                interpretationPolicy: dtr.end.interpretationPolicy,
              }
            : undefined,
          type: dtr.type ?? 'UNKNOWN',
        })),
      })) ?? [],
    carrierRates:
      fragment.carrierRates?.map(carrierRatePreviewFromFragment) ?? [],
    movementEvents:
      fragment.movementEvents?.map((event) => ({
        id: event.id,
        name: String(event.name),
        createdAt: event.createdAt,
        data: event.data
          ? {
              eventType:
                'eventType' in event.data ? String(event.data.eventType) : '',
              exception:
                'exception' in event.data && event.data.exception
                  ? {
                      type: String(event.data.exception.type),
                      data: event.data.exception.data,
                    }
                  : undefined,
            }
          : undefined,
      })) ?? [],
    pricing: fragment.pricing
      ? {
          targetPrice: fragment.pricing.targetPrice ?? undefined,
          maxPrice: fragment.pricing.maxPrice ?? undefined,
        }
      : undefined,
    isTrackingPaused: fragment.isTrackingPaused,
  };
};

const carrierRatePreviewFromFragment = (
  fragment: CarrierRatePreviewFragment
): CarrierRatePreview => {
  return {
    id: fragment.id,
    ratingMethod: fragment.ratingMethod,
    computedRate: (fragment.computedRate as Rate) ?? undefined,
  };
};

export const movementFromFragment = (fragment: MovementFragment): Movement => {
  return {
    id: fragment.id,
    shipmentMovementIdx: fragment.seq,
    carrierRates: fragment.carrierRates.map(carrierRateFromFragment),
    shipment: shipmentFromFragment(fragment.shipment),
    pricing: {
      targetPrice: fragment.pricing?.targetPrice ?? undefined,
      maxPrice: fragment.pricing?.maxPrice ?? undefined,
    },
    commodity: fragment.commodity ?? undefined,
    customerRate: fragment.customerRate ?? undefined,
    inquiryCount: fragment.inquiryCount ?? undefined,
    attributes:
      (fragment.attributes as Record<string, string> | null | undefined) ??
      undefined,
    negotiationStrategyConfigOverride:
      fragment.negotiationStrategyConfigOverride ?? undefined,
  };
};

export const carrierRateFromFragment = (
  fragment: CarrierRateFragment
): CarrierRate => {
  const carrier = fragment.carrier
    ? {
        id: fragment.carrier.id,
        createdAt: fragment.carrier.createdAt,
        name: fragment.carrier.name,
        dotNumber: fragment.carrier.dotNumber ?? '',
        mcNumber: fragment.carrier.mcNumber ?? undefined,
        isInNetwork: fragment.carrier.isInNetwork,
      }
    : undefined;
  const conversation = fragment.conversation
    ? {
        id: fragment.conversation.id,
        type: fragment.conversation.type,
        summary: fragment.conversation.summary
          ? { summary: fragment.conversation.summary.summary ?? undefined }
          : undefined,
      }
    : undefined;
  return {
    ...fragment,
    computedRate: (fragment.computedRate as Rate) ?? undefined,
    carrier,
    contact: fragment.contact
      ? {
          id: fragment.contact.id,
          name: fragment.contact.name,
          email: fragment.contact.email ?? '',
          phone: fragment.contact.phone ?? '',
        }
      : undefined,
    conversation,
  };
};

export const carrierRateWithActionsFromFragment = (
  fragment: CarrierRateWithActionsFragment
): CarrierRate => {
  const carrier = fragment.carrier
    ? {
        id: fragment.carrier.id,
        createdAt: fragment.carrier.createdAt,
        name: fragment.carrier.name,
        dotNumber: fragment.carrier.dotNumber ?? '',
        mcNumber: fragment.carrier.mcNumber ?? undefined,
        isInNetwork: fragment.carrier.isInNetwork,
      }
    : undefined;
  const conversation = fragment.conversation
    ? {
        id: fragment.conversation.id,
        type: fragment.conversation.type,
        summary: fragment.conversation.summary
          ? { summary: fragment.conversation.summary.summary ?? undefined }
          : undefined,
        actions: (fragment.conversation.actions ?? []).map((action) => ({
          output: action.output
            ? (action.output as Record<string, unknown>)
            : undefined,
        })),
      }
    : undefined;
  return {
    ...fragment,
    computedRate: (fragment.computedRate as Rate) ?? undefined,
    carrier,
    contact: fragment.contact
      ? {
          id: fragment.contact.id,
          name: fragment.contact.name,
          email: fragment.contact.email ?? '',
          phone: fragment.contact.phone ?? '',
        }
      : undefined,
    conversation,
  };
};

type MovementWithActionsFragment = MovementWithActionsQuery['movementById'];

export const movementWithActionsFromFragment = (
  fragment: MovementWithActionsFragment
): Movement => {
  const shipment = {
    ...shipmentFromFragment(fragment.shipment),
    externalDeepLink: fragment.shipment.externalDeepLink ?? undefined,
  };
  return {
    id: fragment.id,
    shipmentMovementIdx: fragment.seq,
    carrierRates: fragment.carrierRates.map(carrierRateWithActionsFromFragment),
    shipment,
    pricing: {
      targetPrice: fragment.pricing?.targetPrice ?? undefined,
      maxPrice: fragment.pricing?.maxPrice ?? undefined,
    },
    commodity: fragment.commodity ?? undefined,
    customerRate: fragment.customerRate ?? undefined,
    inquiryCount: fragment.inquiryCount ?? undefined,
    attributes:
      (fragment.attributes as Record<string, string> | null | undefined) ??
      undefined,
    negotiationStrategyConfigOverride:
      fragment.negotiationStrategyConfigOverride ?? undefined,
  };
};

export const movementDetailsFromFragment = (
  fragment: MovementDetailsFragment
): MovementDetails => {
  const fragmentCoverageStatus = isCoverageStatus(fragment.coverageStatus)
    ? fragment.coverageStatus
    : undefined;
  const detailsCoverageStatus = isCoverageStatus(
    fragment.details?.coverageStatus
  )
    ? fragment.details.coverageStatus
    : undefined;
  const details: MovementDetails = {
    ...(fragment.details || {}),
    attributes:
      (fragment.attributes as Record<string, string> | null | undefined) ??
      undefined,
    coverageStatus: fragmentCoverageStatus ?? detailsCoverageStatus,
  };

  details.equipment = toEquipmentArray(details.equipment);

  details.stops =
    details.stops?.map((stop) => ({
      ...stop,
      dateTimeRanges: stop.dateTimeRanges?.map((range) => ({
        ...range,
        start: standardizeTimeZone(range.start),
        end: range.end ? standardizeTimeZone(range.end) : undefined,
      })),
    })) ?? [];

  return details;
};

type HasTZ = { timeZone?: string; timezone?: string };
type HasTimezone = { timezone?: string };

export function standardizeTimeZone<T extends HasTZ>(obj: T): HasTimezone & T {
  const { timeZone, timezone, ...rest } = obj as any;
  return {
    ...rest,
    timezone: timeZone ?? timezone,
  } as T;
}

const toEquipmentArray = (
  equipment?: MovementEquipment | MovementEquipment[]
): MovementEquipment[] | undefined => {
  if (!equipment) {
    return undefined;
  }

  if (Array.isArray(equipment)) {
    return equipment;
  }

  return [equipment];
};
